import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Warehouse, Store, BookmarkCheck, Search, ArrowUpDown, ArrowUp, ArrowDown, Edit2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';

// Format price with Romanian locale (comma as decimal separator)
const formatPrice = (value) => {
    if (value === undefined || value === null) return '0';
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2).replace('.', ',');
};

export const StockGeneral = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [sortBy, setSortBy] = useState('name'); // 'name', 'total_qty', 'price', 'depozit', 'magazin', 'comenzi'
    const [sortDir, setSortDir] = useState('asc'); // 'asc', 'desc'

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/stock-general');
            setProducts(response.data);
        } catch (err) {
            toast.error('Eroare la încărcare');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(field);
            setSortDir(field === 'name' ? 'asc' : 'desc');
        }
    };

    const sortedProducts = [...products]
        .filter(p => 
            !search || 
            p.name?.toLowerCase().includes(search.toLowerCase()) ||
            p.barcode?.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => {
            let valA, valB;
            switch (sortBy) {
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    break;
                case 'total_qty':
                    valA = a.total_qty || 0;
                    valB = b.total_qty || 0;
                    break;
                case 'price':
                    valA = a.price || 0;
                    valB = b.price || 0;
                    break;
                case 'depozit':
                    valA = a.depozit_qty || 0;
                    valB = b.depozit_qty || 0;
                    break;
                case 'magazin':
                    valA = a.magazin_qty || 0;
                    valB = b.magazin_qty || 0;
                    break;
                case 'comenzi':
                    valA = a.comenzi_qty || 0;
                    valB = b.comenzi_qty || 0;
                    break;
                default:
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
            }
            if (sortDir === 'asc') {
                return valA < valB ? -1 : valA > valB ? 1 : 0;
            } else {
                return valA > valB ? -1 : valA < valB ? 1 : 0;
            }
        });

    const totalStock = products.reduce((sum, p) => sum + (p.total_qty || 0), 0);
    const totalValue = products.reduce((sum, p) => {
        const depozitVal = (p.price || 0) * (p.depozit_qty || 0);
        const magazinVal = (p.price || 0) * (p.magazin_qty || 0);
        const comenziVal = p.comenzi_value || 0;  // Use actual final_price from reservations
        return sum + depozitVal + magazinVal + comenziVal;
    }, 0);
    
    // Calculate totals by location
    const depozitTotal = products.reduce((sum, p) => sum + (p.depozit_qty || 0), 0);
    const depozitValue = products.reduce((sum, p) => sum + ((p.price || 0) * (p.depozit_qty || 0)), 0);
    const magazinTotal = products.reduce((sum, p) => sum + (p.magazin_qty || 0), 0);
    const magazinValue = products.reduce((sum, p) => sum + ((p.price || 0) * (p.magazin_qty || 0)), 0);
    const comenziTotal = products.reduce((sum, p) => sum + (p.comenzi_qty || 0), 0);
    const comenziValue = products.reduce((sum, p) => sum + (p.comenzi_value || 0), 0);  // Use actual final_price

    const SortButton = ({ field, label }) => (
        <button
            onClick={() => handleSort(field)}
            className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                sortBy === field ? 'bg-zinc-200 text-zinc-800' : 'text-zinc-500 hover:bg-zinc-100'
            }`}
        >
            {label}
            {sortBy === field ? (
                sortDir === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
            ) : (
                <ArrowUpDown size={12} className="opacity-50" />
            )}
        </button>
    );

    return (
        <div data-testid="stock-general-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Stock General
                    </h1>
                    <p className="text-zinc-500 mt-1">
                        {products.length} produse unice
                    </p>
                </div>
            </div>
            
            {/* Summary Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                {/* Total */}
                <div className="bg-zinc-900 text-white rounded-lg p-3">
                    <div className="text-xs text-zinc-400 mb-1">TOTAL</div>
                    <div className="text-xl font-bold">{formatPrice(totalValue)} <span className="text-sm font-normal">RON</span></div>
                    <div className="text-xs text-zinc-400">{totalStock} buc</div>
                </div>
                {/* Depozit */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <div className="flex items-center gap-1 text-xs text-amber-700 mb-1">
                        <Warehouse size={12} /> DEPOZIT
                    </div>
                    <div className="text-xl font-bold text-amber-800">{formatPrice(depozitValue)} <span className="text-sm font-normal">RON</span></div>
                    <div className="text-xs text-amber-600">{depozitTotal} buc</div>
                </div>
                {/* Magazin */}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <div className="flex items-center gap-1 text-xs text-emerald-700 mb-1">
                        <Store size={12} /> MAGAZIN
                    </div>
                    <div className="text-xl font-bold text-emerald-800">{formatPrice(magazinValue)} <span className="text-sm font-normal">RON</span></div>
                    <div className="text-xs text-emerald-600">{magazinTotal} buc</div>
                </div>
                {/* Comenzi */}
                <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <div className="flex items-center gap-1 text-xs text-violet-700 mb-1">
                        <BookmarkCheck size={12} /> COMENZI
                    </div>
                    <div className="text-xl font-bold text-violet-800">{formatPrice(comenziValue)} <span className="text-sm font-normal">RON</span></div>
                    <div className="text-xs text-violet-600">{comenziTotal} buc</div>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white border border-zinc-200 rounded-lg p-4 mb-6">
                <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Caută produs..."
                        className="pl-10 h-11"
                    />
                </div>
            </div>

            {/* Sort Options */}
            <div className="flex flex-wrap gap-1 mb-3">
                <span className="text-xs text-zinc-500 py-1 mr-1">Sortare:</span>
                <SortButton field="name" label="Nume" />
                <SortButton field="total_qty" label="Total" />
                <SortButton field="depozit" label="Depozit" />
                <SortButton field="magazin" label="Magazin" />
                <SortButton field="comenzi" label="Comenzi" />
            </div>

            {/* Products List */}
            {loading ? (
                <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-5 w-1/3 mb-2" />
                            <Skeleton className="h-4 w-1/2" />
                        </div>
                    ))}
                </div>
            ) : sortedProducts.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <Package size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">
                        {search ? 'Niciun rezultat' : 'Niciun produs în stock'}
                    </h3>
                </div>
            ) : (
                <div className="bg-white border border-zinc-200 rounded-lg divide-y">
                    {sortedProducts.map((product) => (
                        <div
                            key={product.id}
                            className="px-2 py-1.5 hover:bg-zinc-50 transition-colors"
                        >
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                    <span className="font-medium text-sm text-zinc-900 truncate">{product.name}</span>
                                </div>
                                
                                {/* Quantities */}
                                <div className="flex items-center gap-2 text-xs">
                                    <div className="flex items-center gap-0.5 text-amber-600 min-w-[32px] justify-end">
                                        <Warehouse size={12} />
                                        <span className="font-semibold">{product.depozit_qty}</span>
                                    </div>
                                    <div className="flex items-center gap-0.5 text-emerald-600 min-w-[32px] justify-end">
                                        <Store size={12} />
                                        <span className="font-semibold">{product.magazin_qty}</span>
                                    </div>
                                    <div className="flex items-center gap-0.5 text-violet-600 min-w-[32px] justify-end">
                                        <BookmarkCheck size={12} />
                                        <span className="font-semibold">{product.comenzi_qty}</span>
                                    </div>
                                    <div className="text-zinc-700 font-semibold min-w-[24px] text-right border-l border-zinc-200 pl-2">
                                        {product.total_qty}
                                    </div>
                                    <button
                                        onClick={() => navigate(`/products?edit=${product.id}`)}
                                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded ml-1"
                                        title="Editează produs"
                                    >
                                        <Edit2 size={14} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default StockGeneral;
